import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';

import {
	Button,
	GroupSelectSitesCells,
	RadioButton,
	Select,
	SliderCheckbox,
	SpinnerOverlay,
} from '@components/common';
import Granularities from '@components/Kpi/Parameters/Granularities';
import KpiDatesParameters from '@components/Kpi/Parameters/KpiDatesParameters';
import ParametersKpiSelector from '@components/Kpi/Parameters/ParametersKpiSelector/ParametersKpiSelector';
import { useKpiSelectorsLogic } from '@components/Kpi/Parameters/ParametersKpiSelector/useKpiSelectorsLogic';
import { useDynamicDatesLogic } from '@components/Kpi/Parameters/useDynamicDatesLogic';
import { usePresetLogic } from '@components/Kpi/Preset/PresetForm/usePresetLogic';
import { usePresetParametersLogic } from '@components/Kpi/Preset/PresetForm/usePresetParametersLogic';
import { usePresetSitesAndVendorLogic } from '@components/Kpi/Preset/PresetForm/usePresetSitesAndVendorLogic';
import { usePresetTechnologySelectorsLogic } from '@components/Kpi/Preset/PresetForm/usePresetTechnologySelectorsLogic';
import { useSaveLogic } from '@components/Kpi/Preset/PresetForm/useSaveLogic';

import { useQueryKpiGroup } from '@hooks/kpis/useQueryKpiGroup';
import { useQueryOptionsGranularities } from '@hooks/kpis/useQueryOptionsGranularities';
import { useQueryOptionsGranularityAutomatic } from '@hooks/kpis/useQueryOptionsGranularityAutomatic';

import { get5GLabel } from '@utils/common';
import {
	endOfDayInUTC,
	prepareDateForRequest,
	startOfDayInUTC,
} from '@utils/kpi-utils/date';
import { useModal } from '@components/common/Modal/utils';
import { changeOptionsByVendor } from '@components/Kpi/Parameters/utils';

import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { IPresetDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IUpdatePresetDTO } from '@interfaces/kpi/dto/kpiPresetsDTO';
import { IPreset } from '@interfaces/kpi/kpiCommon';
import { IGroupSelectOptions } from '@components/common/GroupSelect/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';
import { IPresetTypesProps } from '@components/Kpi/Preset/Preset.types';

import {
	ETechnologiesLabels,
	ETechnologiesValues,
	EVendorsValues,
} from '@enums/common';
import {
	EAggregationTypes,
	EFormFields,
	TAggregationTypes,
} from '@enums/kpi-api';
import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import {
	HALF_YEAR_DATE_INTERVAL,
	IS_SHARED_PRESET_LABEL,
	TOOLTIP_SHARED_PRESET_INFO,
	TOOLTIP_SHARED_PRESET_IRREVERSIBLE,
	TOOLTIP_SHARED_PRESET_NAME_UNIQUE,
} from '@constants/KPI/kpi-common';
import { getPresetParametersDefaultValues } from '@constants/KPI/parametersDefaultValues';

export interface AdditionalPresetFormProps {
	options5GOran: Array<IGroupSelectOptions>;
	isLoadingOptions: boolean;
	openPresetForm: (presetDate: IPresetDTO) => void;
	optionsVendorsKpi: Array<TSelectOptionType & { isDefault: boolean }>;
	updatePreset: (values: IUpdatePresetDTO) => void;
}

export interface PresetFormProps extends AdditionalPresetFormProps, Partial<IPresetTypesProps> {
	isDirtyForm?: boolean;
	formEditablePreset: IPresetDTO;
	setIsDirtyPresetForm: Dispatch<SetStateAction<boolean>>;
	closePresetForm: () => void;
	selectedPresetValue?: TSelectOptionType<string>;
	setStateSelectedPreset: Dispatch<SetStateAction<IPresetDTO | undefined>>;
	isLoading: boolean;
	maxDateInterval: number;
}

const PresetForm = ({
	formEditablePreset,
	isLoading,
	isLoadingOptions,
	openPresetForm,
	options5GOran,
	optionsVendorsKpi,
	setIsDirtyPresetForm,
	isDirtyForm,
	presets,
	updatePreset,
	closePresetForm,
	selectedPresetValue,
	setStateSelectedPreset,
	maxDateInterval = HALF_YEAR_DATE_INTERVAL,
}: PresetFormProps) => {
	const { onClose } = useModal();

	const [isLoadingSetPreset, setIsLoadingSetPreset] = useState(false);
	const presetValue: TSelectOptionType & IPreset | undefined = useMemo(() => {
		return presets?.find(preset => preset.id === formEditablePreset?.id);
	}, [formEditablePreset?.id, presets]);

	const defaultFrom = startOfDayInUTC(new Date());
	const defaultTo = startOfDayInUTC(new Date());
	const isSharedDefault = formEditablePreset?.isShared ?? false;

	const defaultValues = useMemo(() => getPresetParametersDefaultValues({
		defaultFrom,
		defaultTo,
		formEditablePreset,
	}), [defaultFrom, defaultTo, formEditablePreset]);

	const formMethods = useForm<FieldValues>({
		mode: EValidationMode.all,
		reValidateMode: ERevalidationMode.onChange,
		defaultValues,
	});

	const {
		register,
		setValue,
		getValues,
		resetField,
		watch,
		control,
	} = formMethods;

	const onChangeIsShared = (id: string, checked: boolean) => {
		setValue(id, checked);
		setIsDirtyPresetForm(true);
	};

	const {
		vendor,
		isAutomaticGranularity,
		granularity,
		initTreeKpiData,
		initTreeCountersData,
		treeKpiIds,
		treeCountersIds,
		isOranVendor,
		from,
		to,
		maxSelection,
		minDate,
		includeDateIntervalsFrom,
		includeDateIntervalsTo,
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
		isPassName,
		isLoadingCheckPresetForUpdateIsShared,
		isShared,
	} = usePresetParametersLogic({
		formMethods,
		defaultFrom,
		maxDateInterval,
		presetValue,
	});

	const {
		defaultValueT_5G,
		onChangeSitesT_5G,
		resetT_5G,
	} = usePresetTechnologySelectorsLogic({
		setIsDirtyPresetForm,
		setValue,
		resetField,
	});

	const { kpiGroupData, countersGroupData } = useQueryKpiGroup({
		vendor: vendor?.value,
		isWithMaxSelection: !!maxSelection,
	});

	const {
		treeKpi,
		treeCounters,
		optionsKpi,
		selectedKpiNumber,
		selectedCountersNumber,
		maxSelectionText,
		isMaxSelectionLimitErrorMessageShown,
		isMaxSelectionLimitErrorMessageCountersShown,
		onChangeKpi,
		resetKpi,
		handleUpdateTreeKpi,
		handleUpdateTreeCounters,
		setTreeKpi,
		setTreeCounters,
		setTreeKpiIds,
		setTreeCountersIds,
		setSelectedKpiNumber,
		setSelectedCountersNumber,
		setInitKpiData,
		setInitCountersData,
		//reuse for Parameters and Presets
	} = useKpiSelectorsLogic({
		setIsDirty: setIsDirtyPresetForm,
		kpiGroupData,
		countersGroupData,
		setValue,
		resetField,
		vendor,
		maxSelection,
	});

	const {
		handleCreatePreset,
		handleUpdatePreset,
	} = useSaveLogic({
		getValues,
		vendor,
		presetValue,
		selectedPresetValue,
		formEditablePreset,
		openPresetForm,
		updatePreset,
		closePresetForm,
		setStateSelectedPreset,
	});

	const {
		resetSitesAndVendor,
	} = usePresetSitesAndVendorLogic({
		setValue,
		optionsVendorsKpi,
		onChangeSitesT_5G,
		resetT_5G,
		options5GOran,
		vendor,
	});

	const { optionsGranularities, isLoadingOptionsGranularities } = useQueryOptionsGranularities({
		isOranVendor: true,
		from: prepareDateForRequest(from, startOfDayInUTC),
		to: prepareDateForRequest(to, endOfDayInUTC),
		vendor: EVendorsValues.oran,
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
	});

	const {
		automaticGranularity,
		isLoadingOptionsAutomaticGranularity,
	} = useQueryOptionsGranularityAutomatic({
		isOranVendor: true,
		vendor: EVendorsValues.oran,
		from: prepareDateForRequest(from, startOfDayInUTC),
		to: prepareDateForRequest(to, endOfDayInUTC),
		dateSelectionMode,
		dynamicDateRange,
		startDateOffset,
	});

	const options5G = useMemo(() => {
		return changeOptionsByVendor({
			vendor,
			options5GOran,
			technology: ETechnologiesValues.T_5G,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		vendor?.value,
		options5GOran?.length,
	]);

	const setFromDate = (date: number | null) => {
		setValue(EFormFields.from, date);
		setValue(EFormFields.granularity, null);
		setIsDirtyPresetForm(true);
	};

	const setToDate = (date: number | null) => {
		setValue(EFormFields.to, date);
		setValue(EFormFields.granularity, null);
		setIsDirtyPresetForm(true);
	};

	const onChangeAggregationType = useCallback((value: TAggregationTypes) => {
		setValue(EFormFields.aggregationType, value);

		setIsDirtyPresetForm(true);
	}, [setValue, setIsDirtyPresetForm]);

	const handleUpdateTreeWithWorker = useCallback(
		(tree: IFlatMoTreeNode[], kpiIds: string[]): Promise<IFlatMoTreeNode[]> => {
			const treeUpdaterWorker = new Worker(new URL('../../../Kpi/Parameters/treeUpdaterWorker', import.meta.url), {
				type: 'module',
			});

			return new Promise((resolve) => {
				treeUpdaterWorker.postMessage({ data: tree, kpiIds });

				treeUpdaterWorker.onmessage = (e: MessageEvent<IFlatMoTreeNode[]>) => {
					treeUpdaterWorker.terminate();
					resolve(e.data);
				};
			});
		},
		[],
	);

	const {
		dynamicDatesOptions,
		dateRangePreview,
		isLoadingDynamicDates,
		isLoadingDateRangePreview,
	} = useDynamicDatesLogic({
		vendor,
		getValues,
	});

	const {
		setSelectedPreset,
	} = usePresetLogic({
		formEditablePreset,
		minDate,
		kpiGroupData,
		countersGroupData,
		treeKpi,
		treeCounters,
		setValue,
		resetField,
		resetKpi,
		handleUpdateTreeWithWorker,
		setTreeKpi,
		setTreeCounters,
		setTreeKpiIds,
		setTreeCountersIds,
		setInitKpiData,
		setInitCountersData,
		setIsDirtyPresetForm,
		resetSitesAndVendor,
		defaultFrom,
		defaultTo,
		setIsLoadingSetPreset,
		dynamicDatesOptions,
	});

	useEffect(() => {
		if (granularity) {
			setIsDirtyPresetForm(true);
		}
	}, [granularity, setIsDirtyPresetForm]);

	useEffect(() => {
		void setSelectedPreset();

		setIsDirtyPresetForm(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		setTreeKpi(kpiGroupData);
		setInitKpiData(kpiGroupData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kpiGroupData.length, setInitKpiData]);

	useEffect(() => {
		setTreeCounters(countersGroupData);
		setInitCountersData(countersGroupData);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [countersGroupData.length, setInitCountersData]);

	const isLoadingGranularities = isLoadingOptionsGranularities || isLoadingOptionsAutomaticGranularity;

	const isDisableSaveButtonByGranularity = !(granularity || isAutomaticGranularity);

	const isDisabledSaveButton = isDisableSaveButtonByGranularity || isMaxSelectionLimitErrorMessageShown
		|| isMaxSelectionLimitErrorMessageCountersShown;

	const isLoadingPresetForm = isLoading || isLoadingOptions || isLoadingSetPreset
		|| isLoadingGranularities || isLoadingCheckPresetForUpdateIsShared;

	return (
		<div className='f5gParametersContainer f5gParametersContainerOnMap'>
			{isLoadingPresetForm && <SpinnerOverlay/>}
			<div className='f5gParametersBody'>
				<Select
					disabled
					editTooltip='Edit preset'
					isWithoutUseForms
					minHeight='28px'
					name='preset'
					noOptionsMessage='No presets found'
					options={presets}
					placeholder='Select preset'
					renameTooltip='Rename preset'
					required
					value={presetValue}
				/>
				<div className='f5gParametersTechnology'>
					<div className='f5gParametersBodyLabel'>
						Technology
					</div>
					<SliderCheckbox
						checked={isShared}
						disabled={isSharedDefault || !isPassName}
						id='isShared'
						isLabelAfter
						label={IS_SHARED_PRESET_LABEL}
						onChange={onChangeIsShared}
						tooltipText={!isPassName
							? TOOLTIP_SHARED_PRESET_NAME_UNIQUE
							: isSharedDefault
								? TOOLTIP_SHARED_PRESET_IRREVERSIBLE
								: TOOLTIP_SHARED_PRESET_INFO
						}
					/>
					<GroupSelectSitesCells
						checkboxLabel={ETechnologiesLabels.T_5G}
						defaultOptions={defaultValueT_5G}
						gap='54px'
						isCheckboxTheme
						isInLine
						label='Select nodes/cells'
						modalLabel={`Select node/cells with ${get5GLabel(isOranVendor)}`}
						name={ETechnologiesValues.T_5G}
						onChange={onChangeSitesT_5G}
						options={options5G}
						searchPlaceholder={`Search by node ID/cell ID`}
						selectAllName='select-all-5G-sites'
						withVirtualization
					/>
				</div>
				<ParametersKpiSelector
					handleUpdateTreeCounters={handleUpdateTreeCounters}
					handleUpdateTreeKpi={handleUpdateTreeKpi}
					initTreeCountersData={initTreeCountersData}
					initTreeKpiData={initTreeKpiData}
					isMaxSelectionLimitErrorMessageCountersShown={isMaxSelectionLimitErrorMessageCountersShown}
					isMaxSelectionLimitErrorMessageShown={isMaxSelectionLimitErrorMessageShown}
					isOranVendor
					isRequired={false}
					maxSelection={maxSelection}
					maxSelectionText={maxSelectionText}
					onChangeKpi={onChangeKpi}
					optionsKpi={optionsKpi}
					selectedCountersNumber={selectedCountersNumber}
					selectedKpiNumber={selectedKpiNumber}
					setInitCountersData={setInitCountersData}
					setInitKpiData={setInitKpiData}
					setIsDirty={setIsDirtyPresetForm}
					setSelectedCountersNumber={setSelectedCountersNumber}
					setSelectedKpiNumber={setSelectedKpiNumber}
					setTreeCounters={setTreeCounters}
					setTreeCountersIds={setTreeCountersIds}
					setTreeKpi={setTreeKpi}
					setTreeKpiIds={setTreeKpiIds}
					treeCounters={treeCounters}
					treeCountersIds={treeCountersIds}
					treeKpi={treeKpi}
					treeKpiIds={treeKpiIds}
				/>
				<div className='f5gParametersDisplay'>
					<div className='f5gParametersBodyLabel'>
						Display as:
					</div>
					<div className='f5gParametersDisplayButtons'>
						<div className='f5gParametersDisplayButtonsTitle'>
							Aggregated KPI
						</div>
						<RadioButton
							checked={watch(EFormFields.aggregationType) === EAggregationTypes.aggregated}
							id='aggregatedType'
							label='Aggregated KPI'
							onChange={onChangeAggregationType}
							value={EAggregationTypes.aggregated}
						/>
					</div>
					<div className='f5gParametersDisplayButtons'>
						<div className='f5gParametersDisplayButtonsTitle'>
							node level KPI
						</div>
						<RadioButton
							checked={watch(EFormFields.aggregationType) === EAggregationTypes.siteLevel}
							id='siteLevelType'
							label='Aggregated cells value for a node'
							onChange={onChangeAggregationType}
							value={EAggregationTypes.siteLevel}
						/>
						<RadioButton
							checked={watch(EFormFields.aggregationType) === EAggregationTypes.siteLevelAggregatedCells}
							id='siteLevelAggregatedCellsType'
							label='Separate lines for each cell of a node'
							onChange={onChangeAggregationType}
							value={EAggregationTypes.siteLevelAggregatedCells}
						/>
					</div>
				</div>
				<>
					<KpiDatesParameters
						control={control}
						dateRangePreview={dateRangePreview}
						dynamicDatesOptions={dynamicDatesOptions}
						dynamicId='dynamicModeIdPresetForm'
						from={from}
						includeDateIntervalsFrom={includeDateIntervalsFrom}
						includeDateIntervalsTo={includeDateIntervalsTo}
						isLoadingDateRangePreview={isLoadingDateRangePreview}
						isLoadingDynamicDates={isLoadingDynamicDates}
						isRequired={false}
						minDate={minDate}
						register={register}
						setFromDate={setFromDate}
						setIsDirty={setIsDirtyPresetForm}
						setToDate={setToDate}
						setValue={setValue}
						staticId='staticModeIdPresetForm'
						to={to}
						watch={watch}
					/>
					<Granularities
						automaticGranularity={automaticGranularity}
						automaticId='automaticIdGranularityForm'
						control={control}
						granularities={optionsGranularities}
						granularity={granularity}
						isAutomaticGranularity={isAutomaticGranularity}
						isRequired={false}
						manualId='manualIdGranularityForm'
						setIsDirty={setIsDirtyPresetForm}
						setValue={setValue}
					/>
				</>
			</div>
			<div className='f5gParametersSubmissionButtons'>
				<div className='f5gParametersControlEditButtons'>
					<Button
						large
						onClick={onClose}
						text='Cancel'
						withoutBackground
					/>
					<Button
						disabled={isDisabledSaveButton}
						large
						onClick={handleCreatePreset}
						secondary
						text='Save as new preset'
					/>
					<Button
						disabled={isDisabledSaveButton || !isDirtyForm}
						large
						onClick={handleUpdatePreset}
						text='Save'
					/>
				</div>
			</div>
		</div>
	);
};

export default PresetForm;
